import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import CheckoutLayout from "../../components/CheckoutLayout";
import { Box, Container, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import Step1 from "./Step1";
import './styles.css'
import Step2 from "./Step2";
import Step3 from "./Step3";
import UpdateTourModal from "../../components/UpdateTourModal";
import CheckoutContext from "../../components/CheckoutContext/checkoutContext";
import { useDispatch, useSelector } from "react-redux";
import { remove, selectTours } from "../../app/features/cartSlice";
import { useScreenDetector } from "../../hooks/useScreenDetector";

import BlurImageWrapper, { opacities } from '../../components/BlurImageWrapper'

import trekzyHeroLight from '../../assets/images/t-light.svg'

const Checkout = ({ step }) => {
    const { t } = useTranslation(['checkout', 'components']);
    const steps = [t('step1-name'), t('step2-name'), t('step3-name')];
    const cartTours = useSelector(state => state.cart.checkout.tours);
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    const [open, setOpen] = useState(false);
    const [selectedTour, setSelectedTour] = useState(null);
    const { isMobile } = useScreenDetector()
    const dispatch = useDispatch();
    const location = useLocation();

    const handleRemoveTour = (id) => dispatch(remove(id))

    const handleClose = () => setOpen(false);

    const isStepSkipped = (step) => {
        return skipped.has(step);
    };

    const getTourByIdentifier = (identifier) => {
        if (!identifier) return null;
        let found = cartTours.find(t => t.id === identifier);
        if (found) return found;
        found = cartTours.find(t => t.slug === identifier);
        if (found) return found;
        found = cartTours.find(t => t.name === identifier);
        if (found) return found;
        return null;
    }

    const handleEditTour = (identifier) => {
        const tour = getTourByIdentifier(identifier);
        if (!tour) {
            // eslint-disable-next-line no-console
            console.warn('[Checkout] handleEditTour: tour not found for', identifier);
            return;
        }
        setSelectedTour(tour);
        setOpen(true);
    }

    const handleNext = () => {
        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }

        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
    };

    useEffect(() => {
        setActiveStep(step)
    }, [step])

    // If navigation provided an editTour slug (from addToCart), open the editor for that tour
    useEffect(() => {
        const editTour = location?.state?.editTour;
        if (editTour) {
            // Call handleEditTour with the identifier (slug)
            handleEditTour(editTour);
            // Clear it from history state to avoid re-triggering if user navigates back
            try {
                window.history.replaceState({}, document.title, window.location.pathname);
            } catch (e) {
                // ignore
            }
        }
    }, [location?.state]);

    return (
        <CheckoutContext.Provider value={{
            cartTours: cartTours,
            handleEditTour: handleEditTour,
            handleRemoveTour: handleRemoveTour
        }}>
            {selectedTour && <UpdateTourModal open={open} handleClose={handleClose} tour={selectedTour} isMobile={isMobile} />}

            <BlurImageWrapper imageUrl={trekzyHeroLight} opacity={opacities.none} classType="bgimg-container-gray">
                <CheckoutLayout>
                    <Helmet>
                        <title>{t('step3-page')}</title>
                    </Helmet>
                    <Helmet>
                        <meta
                            name="robots"
                            content="noindex"
                            data-react-helmet="true"
                        />
                    </Helmet>
                    <Container id="checkout-container"
                    // className="background-container"
                    >
                        {/* <img src="/assets/images/t-light.svg" alt="decorativo" className="background-svg" /> */}
                        <Box className="contenido" sx={{ width: '100%', mt: { xs: '40px', md: '60px' } }}>
                            {/* <div className='trekzy-background' /> */}
                            <Stepper
                                activeStep={activeStep}
                                alternativeLabel={isMobile}
                                sx={{
                                    '& .MuiStepIcon-root': {
                                        fontSize: 40
                                    },
                                    '& .MuiStepIcon-text': {
                                        fontSize: '0.8rem'
                                    }
                                }}
                            >
                                {steps.map((label, index) => {
                                    const stepNumber = String(index + 1).padStart(2, '0');
                                    return (
                                        <Step key={label}>
                                            <StepLabel StepIconProps={{ icon: stepNumber }}>
                                                <Typography component="h2">
                                                    {label}
                                                </Typography>
                                            </StepLabel>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                            <Box sx={{ mt: { xs: '40px', md: '20px' } }}>
                                {activeStep === 0 &&
                                    <Step1 />
                                }
                                {activeStep === 1 &&
                                    <Step2 />
                                }
                                {activeStep === 2 &&
                                    <Step3 />
                                }
                            </Box>
                        </Box>
                    </Container>
                </CheckoutLayout>
            </BlurImageWrapper>
        </CheckoutContext.Provider>
    )

}

export default Checkout;
